/* eslint-disable max-lines-per-function */
import React from 'react';

import {
  Minus,
  PanelRight,
  PanelRightClose,
  PictureInPicture,
  Sparkles,
  SquarePen,
} from 'lucide-react';

import type { ChatSidebarMode } from '@/contexts/ChatSidebarContextDef';
import { useHandleShortcutClick } from '@/hooks/useHandleShortcutClick';
import { useShortcuts } from '@/hooks/useShortcuts';
import { useIsDesktop } from '@/hooks/useViewportBreakpoints';
import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ChatHeaderButtonsProps {
  mode: ChatSidebarMode;
  showEmptyState: boolean;
  onNewConversation: () => void;
  onSwitchMode: () => void;
  onClose: () => void;
}

interface IconButtonProps {
  icon: React.ReactNode;
  label: string;
  title: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}

const IconButton = (props: IconButtonProps) => (
  <Button
    variant="ghost"
    size="icon"
    className={cn(
      'h-9 w-9',
      props.disabled &&
        '!pointer-events-auto cursor-not-allowed hover:bg-transparent',
      props.className
    )}
    onClick={props.onClick}
    aria-label={props.label}
    disabled={props.disabled}
    title={props.title}
  >
    {props.icon}
  </Button>
);

export const ChatHeaderButtons = (props: ChatHeaderButtonsProps) => {
  const isDesktop = useIsDesktop();
  const shortcuts = useShortcuts();
  const handleShortcutClick = useHandleShortcutClick();

  const hasShortcuts = shortcuts.length > 0;

  const getSwitchModeTitle = () => {
    if (!isDesktop) {
      return 'Sidebar mode unavailable on small screens';
    }
    return props.mode === 'floating'
      ? 'Switch to sidebar'
      : 'Switch to floating';
  };

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <IconButton
        icon={<SquarePen className="h-4 w-4" />}
        label="Start new conversation"
        title={
          props.showEmptyState
            ? 'Already on new conversation'
            : 'Start new conversation'
        }
        onClick={props.onNewConversation}
        disabled={props.showEmptyState}
      />
      <IconButton
        icon={
          props.mode === 'floating' ? (
            <PanelRight className="h-4 w-4" />
          ) : (
            <PictureInPicture className="h-4 w-4" />
          )
        }
        label={
          props.mode === 'floating'
            ? 'Switch to sidebar mode'
            : 'Switch to floating mode'
        }
        title={getSwitchModeTitle()}
        onClick={props.onSwitchMode}
        disabled={!isDesktop}
      />
      <Tooltip>
        <DropdownMenu>
          <TooltipTrigger asChild>
            <span
              className="inline-flex"
              tabIndex={hasShortcuts ? -1 : 0}
              role={hasShortcuts ? undefined : 'button'}
              aria-label={hasShortcuts ? undefined : 'Contextual shortcuts'}
              aria-disabled={hasShortcuts ? undefined : true}
            >
              <DropdownMenuTrigger asChild disabled={!hasShortcuts}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('h-9 w-9', {
                    'cursor-not-allowed opacity-50 hover:bg-transparent':
                      !hasShortcuts,
                  })}
                  aria-label="Contextual shortcuts"
                  aria-hidden={hasShortcuts ? undefined : true}
                  tabIndex={hasShortcuts ? undefined : -1}
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </span>
          </TooltipTrigger>
          <DropdownMenuContent align="end">
            {shortcuts.map((shortcut) => {
              const Icon = shortcut.icon;
              return (
                <DropdownMenuItem
                  key={shortcut.id}
                  className="gap-3 cursor-pointer"
                  onSelect={() => handleShortcutClick(shortcut)}
                >
                  <Icon className="size-4 shrink-0 text-brand" />
                  <span className="truncate">{shortcut.label}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <TooltipContent>
          {hasShortcuts
            ? 'Contextual shortcuts'
            : 'Shortcuts are not available yet on this page'}
        </TooltipContent>
      </Tooltip>
      <IconButton
        icon={
          props.mode === 'floating' ? (
            <Minus className="h-4 w-4" />
          ) : (
            <PanelRightClose className="h-4 w-4" />
          )
        }
        label={
          props.mode === 'floating' ? 'Close chat window' : 'Close chat sidebar'
        }
        title={
          props.mode === 'floating' ? 'Hide conversation' : 'Collapse sidebar'
        }
        onClick={props.onClose}
      />
    </div>
  );
};
